import { FC, memo, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import arrowOpen from '@assets/images/arrow-open.svg'
import { ManageSearch as ManageSearchIcon, WebAsset as WebAssetIcon } from '@mui/icons-material'
import {
  Box,
  Button,
  CardActions,
  CardContent,
  IconButton,
  IconButtonProps,
  Typography,
} from '@mui/material'
import { indigo } from '@mui/material/colors'
import { styled } from '@mui/material/styles'

import { CustomSvgIcon } from '@components/CustomSvgIcon'
import { FormContext } from '@components/DisplayForm'
import { ObjectFieldChangesTable } from '@components/DisplayForm/components/ObjectFieldChangesTable'
import { useDisplayFormState } from '@components/DisplayForm/hooks'

import { setOpenedContainerForm, setTabIndexInContainer } from '@redux/reducers/formStates.reducer'

import { useAppDispatch, useAppSelector, useSubscribeEvent } from '@hooks'
import {
  FLEXIBLE,
  FORM_ELEMENT_TYPE,
  INITIAL_TAB_HEIGHT,
  INITIAL_TAB_HEIGHT_DIALOG,
} from '@constants'

import { EVENT_NAME, publish } from '../../../../events'
import { FormContainerEnriched, FormDataEnriched, FormTab } from '../../../../types'
import { TabPanel } from '../../../Tabs'
import CardContainer from '../../../ui/CardContainer/CardContainer'
import { ContainerTabPanel } from '../ContainerTabPanel'

type DraggableContainerOnFormProps = {
  container: FormContainerEnriched
  index: number
  availableFormHeight: number
}

type ExpandMoreProps = {
  expand: boolean
} & IconButtonProps

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props

  return <IconButton {...other} />
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
  marginRight: '20px',
  '& > .MuiIcon-root': {
    background: expand ? theme.palette.background.paper : 'transparent',
    boxShadow: !expand ? '2px 2px 6px 0px #000000' : '-2px -2px 6px 0px #000000',
  },
}))

type TabButtonProps = {
  tab: FormTab<FormDataEnriched>
  index: number
  isSelected: boolean
  isFormDialogWindow: boolean
  onTabClick: (index: number) => void
}

// TabButton вынесен за пределы компонента для предотвращения пересоздания при ререндерах
const TabButton: FC<TabButtonProps> = memo(
  ({ tab, index, isSelected, isFormDialogWindow, onTabClick }) => (
    <Button
      sx={{
        height: isFormDialogWindow ? '25px' : '36px',
        color: theme => theme.palette.color?.formCardTab,
        background: theme =>
          isSelected
            ? theme.palette.background.formCardSelectedTab
            : theme.palette.background.formCardTab,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        mr: 0.5,
        ':hover': {
          background: theme => theme.palette.background.formCardSelectedTab,
        },
      }}
      onClick={() => onTabClick(index)}
    >
      <Typography
        sx={{ textTransform: 'none' }}
        onDoubleClick={event => {
          event.stopPropagation()
        }}
      >
        {tab.title}
      </Typography>
    </Button>
  )
)

TabButton.displayName = 'TabButton'

export const ContainerOnForm: FC<DraggableContainerOnFormProps> = ({
  container,
  index,
  availableFormHeight,
}) => {
  const contentPaddingOffset = 5
  const { t } = useTranslation()
  const formContainers = useAppSelector(state => state.formStates)
  const dispatch = useAppDispatch()
  const currentContainerHistory = formContainers?.[container.formCode]?.containers?.[container.id]

  const {
    methods,
    formDataId,
    withPassedParams,
    objectId,
    onSetOpenedContainersInStaticForms,
    samePageFormsData,
    selectedRowIds,
    dialogId,
    isLoadingRow,
  } = useContext(FormContext)
  const { isFormDialogWindow } = useDisplayFormState()

  const [expanded, setExpanded] = useState(Boolean(currentContainerHistory?.open))
  const [currentTabButton, setCurrentTabButton] = useState(
    currentContainerHistory?.currentTabIndex || 0
  )
  const [maxTabsWidth, setTabsMaxWidth] = useState(0)
  const isHistoryContainer = container.history
  const isExpandable = container.expandable
  const titleHidden = container.titleHidden
  const showCardContent = isExpandable ? expanded : true
  const isTabPanelHidden = container.tabs.length === 1 && container.tabs[0].titleHidden

  const minHeight = isFormDialogWindow ? INITIAL_TAB_HEIGHT_DIALOG : INITIAL_TAB_HEIGHT

  // Эффект для установки состояния expanded и currentTabButton из данных статических форм
  useEffect(() => {
    // TODO скорее всего нужно убрать зависимость от samePageFormsData, это должно храниться где то вне формы
    if (!samePageFormsData) {
      return
    }

    const currentOpenedSamePageForm = samePageFormsData.find(data => data.objectId === objectId)

    if (!currentOpenedSamePageForm) {
      return
    }

    const currentContainerInData = currentOpenedSamePageForm.openedContainers.find(
      containerData => containerData.containerIndex === index
    )

    if (currentContainerInData !== undefined) {
      setExpanded(true)
      setCurrentTabButton(currentContainerInData.openedTabIndex)
    }
  }, [samePageFormsData, selectedRowIds])

  useSubscribeEvent(EVENT_NAME.CONFIGURED_FORM_TOGGLE_OPENED_CONTAINERS, data => {
    if (data.detail.containerId) {
      if (data.detail.containerId === container.id) {
        setExpanded(data.detail.expanded)
      }

      return
    }

    if (!isFormDialogWindow) {
      setExpanded(data.detail.expanded)

      return
    }

    if (data.detail.dialogId === dialogId) {
      setExpanded(data.detail.expanded)
    }
  })

  const tabsWrapperRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) {
      return
    }

    const observer = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      setTabsMaxWidth(entries[0].contentRect.width - contentPaddingOffset)
    })

    observer.observe(node)
  }, [])

  const handleSetSamePageFormsData = useCallback(
    (containerExpanded: boolean, openedTabIndex: number) => {
      if (withPassedParams && onSetOpenedContainersInStaticForms) {
        onSetOpenedContainersInStaticForms({
          formDataId: formDataId as number,
          containerExpanded,
          containerIndex: index,
          openedTabIndex,
        })
      }
    },
    [withPassedParams, onSetOpenedContainersInStaticForms, formDataId, index]
  )

  // Используем ref для expanded, чтобы не пересоздавать handleTabClick при каждом открытии/закрытии
  const expandedRef = useRef(expanded)
  expandedRef.current = expanded

  const handleTabClick = useCallback(
    (tabIndex: number) => {
      setCurrentTabButton(() => {
        handleSetSamePageFormsData(expandedRef.current, tabIndex)
        dispatch(
          setTabIndexInContainer({
            containerId: container.id,
            formCode: container.formCode,
            tabIndex,
          })
        )

        return tabIndex
      })
    },
    [handleSetSamePageFormsData, container.id, container.formCode]
  )

  const handleExpandClick = (expanded: boolean) => {
    setExpanded(!expanded)
    dispatch(
      setOpenedContainerForm({
        containerId: container.id,
        formCode: container.formCode,
        open: !expanded,
      })
    )
    handleSetSamePageFormsData(!expanded, currentTabButton)
    publish(EVENT_NAME.CONFIGURED_FORM_HAS_OPENED_CONTAINER, !expanded)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (expanded && e.key === 'Enter') {
      e.preventDefault()
    }
  }

  const Title = (
    <>
      {isHistoryContainer ? (
        <ManageSearchIcon sx={{ color: indigo[500], mr: 1 }} />
      ) : (
        <WebAssetIcon color={'secondary'} sx={{ mr: 1 }} />
      )}
      {!titleHidden && (
        <Typography
          sx={theme => ({
            userSelect: 'none',
            cursor: 'pointer',
            color: theme.palette.color.formCardTab,
            fontWeight: 600,
          })}
        >
          {container.title}
        </Typography>
      )}
    </>
  )

  return (
    <Box sx={{ ...FLEXIBLE, pb: isFormDialogWindow ? 0.5 : 1 }}>
      <CardContainer>
        {isExpandable && (
          <CardActions
            disableSpacing
            sx={{
              padding: isFormDialogWindow ? 0 : 1,
              height: isFormDialogWindow ? '25px' : '42px',
              cursor: 'pointer',
              background: theme => theme.palette.background.formCardContainer,
              border: theme => theme.palette.border.formCardContainer,
            }}
            onClick={() => handleExpandClick(expanded)}
            onKeyDown={handleKeyDown}
          >
            <ExpandMore expand={expanded} onClick={() => handleExpandClick(expanded)}>
              <CustomSvgIcon fontSize={isFormDialogWindow ? 'small' : 'medium'} src={arrowOpen} />
            </ExpandMore>
            {Title}
          </CardActions>
        )}
        {/* TODO FlexBox игнорируется в Collapse, лучше использовать CSSTransition, пока оставил без анимаций
        https://reactcommunity.org/react-transition-group/css-transition
        
        <Collapse unmountOnExit in={expanded} timeout={0}> */}
        {!titleHidden && !isExpandable && (
          <Box sx={{ display: 'flex', pointerEvents: 'none', pl: 1 }}>{Title}</Box>
        )}
        {showCardContent && (
          <CardContent ref={tabsWrapperRef} sx={{ ...FLEXIBLE, p: 0 }}>
            <Box sx={{ ...FLEXIBLE, pb: 1 }}>
              {isHistoryContainer && <ObjectFieldChangesTable />}
              {!isHistoryContainer && (
                <Box
                  sx={{
                    paddingTop: isFormDialogWindow ? 0.5 : 1,
                    background: theme => theme.palette.background.formCardBehindTab,
                    display: 'flex',
                    alignItems: 'end',
                    //maxWidth: maxTabsWidth,
                    height: 'auto',
                    flexWrap: 'wrap',
                    gap: '5px 0',
                  }}
                >
                  {!isTabPanelHidden &&
                    container.tabs.map((tab, tabIndex) => (
                      <TabButton
                        key={tab.id}
                        index={tabIndex}
                        isFormDialogWindow={isFormDialogWindow}
                        isSelected={currentTabButton === tabIndex}
                        tab={tab}
                        onTabClick={handleTabClick}
                      />
                    ))}
                </Box>
              )}
              {!isHistoryContainer && (
                <Box sx={FLEXIBLE}>
                  {container.tabs.map((tab, tabIndex) => {
                    // eslint-disable-next-line react/prop-types
                    const containsListControl = tab.lines.some(({ elements }) =>
                      elements.some(({ type }) => type === FORM_ELEMENT_TYPE.VIEW)
                    )

                    // eslint-disable-next-line react/prop-types
                    const countListControls = tab.lines.reduce(
                      (count, { elements }) =>
                        elements.some(({ type }) => type === FORM_ELEMENT_TYPE.VIEW)
                          ? count + 1
                          : count,
                      0
                    )

                    return (
                      <TabPanel
                        // eslint-disable-next-line react/prop-types
                        key={tab.id}
                        index={currentTabButton}
                        sx={FLEXIBLE}
                        value={tabIndex}
                        contentSx={{
                          ...FLEXIBLE,
                          p: 0,
                        }}
                      >
                        <FormProvider {...methods}>
                          <ContainerTabPanel
                            contentHeight={availableFormHeight}
                            initialHeight={minHeight}
                            isDialogWindow={isFormDialogWindow}
                            tab={tab}
                          />
                        </FormProvider>
                      </TabPanel>
                    )
                  })}
                </Box>
              )}
            </Box>
          </CardContent>
        )}
      </CardContainer>
    </Box>
  )
}
